import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dataService } from '../../services/dataService';
import { InsulinScheduleView } from '../../types/domain';
import { BADGES, POINTS, POINT_RULE_LABELS, PointRule } from '../../lib/gamification';
import { BadgeTile, useGamification } from '../components/Gamification';

const RULE_ORDER: PointRule[] = ['glucose', 'insulin', 'rotation', 'completeDay', 'streakDay'];

/** How each rule is earned, in words a parent can check against the score. */
const RULE_DETAILS: Record<PointRule, string> = {
  glucose: 'Satu poin untuk tiap waktu cek (mis. pre sarapan) per hari.',
  insulin: 'Satu poin untuk tiap jadwal suntikan per hari.',
  rotation: 'Suntik di titik yang berbeda dari suntikan sebelumnya.',
  completeDay: 'Gula darah dicek dan semua jadwal insulin tercatat hari itu.',
  streakDay: 'Tiap hari yang menyambung catatan hari sebelumnya.'
};

/** FR-19: points, streak and badges, with the rules spelled out. */
export default function Achievements() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [schedule, setSchedule] = useState<InsulinScheduleView[] | null>(null);
  const summary = useGamification(user?.id, schedule);

  useEffect(() => {
    if (!user) {
      return;
    }
    let cancelled = false;
    dataService
      .getSchedule(user.id)
      .catch(() => [])
      .then(entries => {
        if (!cancelled) {
          setSchedule(entries);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!summary) {
    return (
      <div className="screen min-h-[100dvh] h-full">
        <div className="screen-card text-center">
          <div className="subtitle">Memuat prestasi...</div>
        </div>
      </div>
    );
  }

  const today = summary.today;

  return (
    <div className="screen min-h-[100dvh] h-full">
      <div className="screen-card" role="group" aria-labelledby="achievements-title">
        <div id="achievements-title" className="section-title">
          Prestasi Adik 🌟
        </div>
        <div className="subtitle">Catat setiap hari untuk mengumpulkan poin dan lencana!</div>

        <div className="game-hero">
          <div className="game-hero-points">
            <span className="game-hero-value">{summary.totalPoints}</span>
            <span className="game-hero-label">Total poin</span>
          </div>
          <div className="game-hero-streak">
            <span className="game-hero-flame" aria-hidden="true">🔥</span>
            <span>
              <strong>{summary.currentStreak} hari</strong> beruntun
            </span>
            <span className="game-hero-note">Terpanjang: {summary.longestStreak} hari</span>
          </div>
        </div>

        <div className="subtitle game-section-title">Lencana</div>
        <div className="game-badge-grid">
          {summary.badges.map(badge => (
            <BadgeTile key={badge.id} badge={badge} />
          ))}
        </div>

        <div className="subtitle game-section-title">Misi hari ini</div>
        <ul className="reminder-list">
          <li className={`reminder reminder--${today.hasGlucose ? 'done' : 'due'}`}>
            <span className="reminder-icon" aria-hidden="true">
              {today.hasGlucose ? '✓' : '!'}
            </span>
            <span className="reminder-text">
              <span className="reminder-title">Cek gula darah</span>
              <span className="reminder-detail">+{today.points.glucose} poin hari ini</span>
            </span>
            {!today.hasGlucose ? (
              <button type="button" className="reminder-action" onClick={() => navigate('/check-glucose')}>
                Catat
              </button>
            ) : null}
          </li>
          <li className={`reminder reminder--${today.hasInsulin ? 'done' : 'due'}`}>
            <span className="reminder-icon" aria-hidden="true">
              {today.hasInsulin ? '✓' : '!'}
            </span>
            <span className="reminder-text">
              <span className="reminder-title">Catat suntikan insulin</span>
              <span className="reminder-detail">
                +{today.points.insulin} poin insulin · +{today.points.rotation} poin rotasi
              </span>
            </span>
            {!today.hasInsulin ? (
              <button type="button" className="reminder-action" onClick={() => navigate('/inject-insulin')}>
                Catat
              </button>
            ) : null}
          </li>
          <li className={`reminder reminder--${today.complete ? 'done' : 'soon'}`}>
            <span className="reminder-icon" aria-hidden="true">
              {today.complete ? '✓' : '•'}
            </span>
            <span className="reminder-text">
              <span className="reminder-title">Lengkapi catatan harian</span>
              <span className="reminder-detail">
                {today.complete
                  ? `Bonus +${POINTS.completeDay} poin didapat!`
                  : `Dapatkan bonus +${POINTS.completeDay} poin`}
              </span>
            </span>
          </li>
        </ul>

        <div className="subtitle game-section-title">Cara dapat poin</div>
        <ul className="game-rules">
          {RULE_ORDER.map(rule => (
            <li key={rule} className="game-rule">
              <span className="game-rule-points">+{POINTS[rule]}</span>
              <span className="game-rule-text">
                <span className="game-rule-title">{POINT_RULE_LABELS[rule]}</span>
                <span className="game-rule-detail">{RULE_DETAILS[rule]}</span>
              </span>
              <span className="game-rule-earned" aria-label={`${summary.breakdown[rule]} poin didapat`}>
                {summary.breakdown[rule]}
              </span>
            </li>
          ))}
          {BADGES.map(badge => (
            <li key={badge.id} className="game-rule">
              <span className="game-rule-points" aria-hidden="true">{badge.emoji}</span>
              <span className="game-rule-text">
                <span className="game-rule-title">{badge.title}</span>
                <span className="game-rule-detail">
                  Konsisten mencatat {badge.days} hari berturut-turut.
                </span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
